import { env } from "@postdaily/env/web";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@postdaily/ui/components/avatar";
import { Badge } from "@postdaily/ui/components/badge";
import { Button } from "@postdaily/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@postdaily/ui/components/card";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@postdaily/ui/components/field";
import { Input } from "@postdaily/ui/components/input";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { Spinner } from "@postdaily/ui/components/spinner";
import { useForm } from "@tanstack/react-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  BadgeCheck,
  Camera,
  MailWarning,
  RefreshCw,
  Save,
  Send,
} from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { PageHeader } from "@/components/page-header";
import { getAuthCallbackUrl } from "@/lib/auth-callback-url";
import { authClient } from "@/lib/auth-client";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/settings/profile")({
  component: ProfileSettingsPage,
});

const AVATAR_ACCEPT = "image/jpeg,image/png,image/webp";
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

function ProfileSettingsPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const viewer = useQuery(trpc.viewer.me.queryOptions());

  // The sidebar/nav user chip reads from the bootstrap query — refresh both
  // so a profile change shows up everywhere immediately.
  const refreshViewer = () => {
    void queryClient.invalidateQueries(trpc.viewer.me.queryFilter());
    void queryClient.invalidateQueries(trpc.viewer.bootstrap.queryFilter());
  };

  if (viewer.isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          size="lg"
          title="Profile"
          description="Your name, photo, and email address."
        />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!viewer.data) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        size="lg"
        title="Profile"
        description="Your name, photo, and email address."
      />
      <AvatarCard
        image={viewer.data.user.image}
        name={viewer.data.user.name}
        onChanged={refreshViewer}
      />
      <NameCard name={viewer.data.user.name} onChanged={refreshViewer} />
      <EmailCard
        email={viewer.data.user.email}
        emailVerified={viewer.data.user.emailVerified}
      />
    </div>
  );
}

function AvatarCard({
  image,
  name,
  onChanged,
}: {
  image: string | null;
  name: string;
  onChanged: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  async function handleFile(file: File) {
    if (!AVATAR_ACCEPT.split(",").includes(file.type)) {
      toast.error("Choose a JPEG, PNG, or WebP image.");
      return;
    }

    if (file.size > AVATAR_MAX_BYTES) {
      toast.error("Choose an image that is 5 MB or smaller.");
      return;
    }

    setIsUploading(true);

    try {
      const response = await fetch(
        `${env.VITE_SERVER_URL}/api/account/avatar`,
        {
          body: file,
          credentials: "include",
          headers: { "content-type": file.type },
          method: "POST",
        },
      );

      if (!response.ok) {
        // SAFETY: the avatar endpoint answers a failure with `{ error }`.
        // The `.catch` already covers a body that is not JSON at all, and the
        // read below falls back to a generic message.
        const body = (await response.json().catch(() => null)) as {
          error?: string;
        } | null;
        toast.error(body?.error ?? "Could not upload your photo. Try again.");
        return;
      }

      toast.success("Profile photo updated");
      onChanged();
    } catch {
      toast.error("Could not upload your photo. Try again.");
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile photo</CardTitle>
        <CardDescription>
          Shown next to your name across the app. JPEG, PNG, or WebP up to 5 MB.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex items-center gap-4">
        <Avatar className="size-16">
          {image ? <AvatarImage src={image} alt={name} /> : null}
          <AvatarFallback />
        </Avatar>
        <input
          ref={fileInputRef}
          type="file"
          accept={AVATAR_ACCEPT}
          className="hidden"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            event.currentTarget.value = "";

            if (file) {
              void handleFile(file);
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          disabled={isUploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {isUploading ? (
            <Spinner data-icon="inline-start" />
          ) : (
            <Camera data-icon="inline-start" />
          )}
          {isUploading ? "Uploading..." : "Upload photo"}
        </Button>
      </CardContent>
    </Card>
  );
}

const nameFormSchema = z.object({
  name: z.string().trim().min(1, "Enter your name").max(120),
});

function NameCard({
  name,
  onChanged,
}: {
  name: string;
  onChanged: () => void;
}) {
  const form = useForm({
    defaultValues: { name },
    onSubmit: async ({ value }) => {
      const { error } = await authClient.updateUser({
        name: value.name.trim(),
      });

      if (error) {
        toast.error(error.message || "Could not update your name.");
        return;
      }

      toast.success("Name updated");
      onChanged();
    },
    validators: {
      onSubmit: nameFormSchema,
    },
  });

  return (
    <Card>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <CardHeader className="border-b">
          <CardTitle>Name</CardTitle>
          <CardDescription>
            Your display name, visible to your workspace members.
          </CardDescription>
        </CardHeader>
        <CardContent className="py-6">
          <form.Field name="name">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <Field className="max-w-sm" data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Full name</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    aria-invalid={isInvalid}
                    autoComplete="name"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  {isInvalid ? (
                    <FieldError errors={field.state.meta.errors} />
                  ) : null}
                </Field>
              );
            }}
          </form.Field>
        </CardContent>
        <CardFooter>
          <form.Subscribe
            selector={(state) => ({
              canSubmit: state.canSubmit,
              isSubmitting: state.isSubmitting,
            })}
          >
            {({ canSubmit, isSubmitting }) => (
              <Button type="submit" disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? (
                  <Spinner data-icon="inline-start" />
                ) : (
                  <Save data-icon="inline-start" />
                )}
                {isSubmitting ? "Saving..." : "Save changes"}
              </Button>
            )}
          </form.Subscribe>
        </CardFooter>
      </form>
    </Card>
  );
}

const emailFormSchema = z.object({
  newEmail: z.email("Enter a valid email address"),
});

function EmailCard({
  email,
  emailVerified,
}: {
  email: string;
  emailVerified: boolean;
}) {
  const [isResending, setIsResending] = useState(false);

  const form = useForm({
    defaultValues: { newEmail: "" },
    onSubmit: async ({ value }) => {
      const { error } = await authClient.changeEmail({
        callbackURL: getAuthCallbackUrl("/settings/profile"),
        newEmail: value.newEmail.trim(),
      });

      if (error) {
        toast.error(
          error.message ||
            "Could not start the email change. You may need to sign in again.",
        );
        return;
      }

      form.reset();
      toast.success(
        "Almost there — approve the change from your CURRENT inbox, then verify the new address from its inbox.",
        { duration: 10000 },
      );
    },
    validators: {
      onSubmit: emailFormSchema,
    },
  });

  async function handleResendVerification() {
    setIsResending(true);

    try {
      const { error } = await authClient.sendVerificationEmail({
        email,
        callbackURL: getAuthCallbackUrl("/settings/profile"),
      });

      if (error) {
        toast.error(error.message || error.statusText);
        return;
      }

      toast.success("Verification email sent");
    } catch (error: unknown) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to send verification email.",
      );
    } finally {
      setIsResending(false);
    }
  }

  return (
    <Card>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <CardHeader className="border-b">
          <CardTitle>Email address</CardTitle>
          <CardDescription>
            Used to sign in and receive notifications.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6 py-6">
          <Field>
            <FieldLabel>Current email</FieldLabel>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm">{email}</span>
              {emailVerified ? (
                <Badge variant="secondary">
                  <BadgeCheck data-icon="inline-start" />
                  Verified
                </Badge>
              ) : (
                <Badge variant="destructive">
                  <MailWarning data-icon="inline-start" />
                  Not verified
                </Badge>
              )}
            </div>
            {emailVerified ? null : (
              <FieldDescription>
                Check your inbox to verify your email address. Some features may
                be limited until you do.
              </FieldDescription>
            )}
          </Field>
          <form.Field name="newEmail">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <Field className="max-w-sm" data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>New email</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="email"
                    aria-invalid={isInvalid}
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <FieldDescription>
                    We'll send an approval link to your current email first,
                    then a verification link to the new one.
                  </FieldDescription>
                  {isInvalid ? (
                    <FieldError errors={field.state.meta.errors} />
                  ) : null}
                </Field>
              );
            }}
          </form.Field>
        </CardContent>
        <CardFooter className="flex flex-wrap gap-2">
          <form.Subscribe
            selector={(state) => ({
              canSubmit: state.canSubmit,
              isSubmitting: state.isSubmitting,
              newEmail: state.values.newEmail,
            })}
          >
            {({ canSubmit, isSubmitting, newEmail }) => (
              <Button
                type="submit"
                disabled={
                  !canSubmit || isSubmitting || newEmail.trim().length === 0
                }
              >
                {isSubmitting ? (
                  <Spinner data-icon="inline-start" />
                ) : (
                  <Send data-icon="inline-start" />
                )}
                {isSubmitting ? "Sending..." : "Change email"}
              </Button>
            )}
          </form.Subscribe>
          {emailVerified ? null : (
            <Button
              type="button"
              variant="outline"
              disabled={isResending}
              onClick={() => {
                void handleResendVerification();
              }}
            >
              {isResending ? (
                <Spinner data-icon="inline-start" />
              ) : (
                <RefreshCw data-icon="inline-start" />
              )}
              {isResending ? "Sending..." : "Resend verification email"}
            </Button>
          )}
        </CardFooter>
      </form>
    </Card>
  );
}
